import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';
import { InboxCard } from './InboxCard.js';

interface Draft {
  message: string;
  mediaAssetId?: string;
  mediaFilename?: string;
}

/**
 * Publishing comments as the Page.
 *
 * The flow deliberately starts from *your* Page and *your* post. There is no
 * field anywhere for typing an arbitrary post id — commenting is only ever
 * possible on content you administer.
 */
export function CommentsPanel() {
  const { t } = useI18n();

  const [pageId, setPageId] = useState('');
  const [sourcePostId, setSourcePostId] = useState('');
  const [parentCommentId, setParentCommentId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([{ message: '' }]);
  const [result, setResult] = useState<string | null>(null);

  const capabilities = useQuery({
    queryKey: ['comment-capabilities'],
    queryFn: api.commentCapabilities,
  });

  const pages = useQuery({ queryKey: ['pages'], queryFn: api.pages });

  const posts = useQuery({
    queryKey: ['owned-posts', pageId],
    queryFn: () => api.ownedPosts(pageId),
    enabled: pageId !== '',
  });

  const existing = useQuery({
    queryKey: ['post-comments', sourcePostId],
    queryFn: () => api.postComments(sourcePostId),
    enabled: sourcePostId !== '' && capabilities.data?.repliesEnabled === true,
  });

  const publish = useMutation({
    mutationFn: () =>
      api.createCommentJob({
        targetKind: parentCommentId ? 'COMMENT' : 'POST',
        sourcePostId,
        parentCommentId: parentCommentId ?? undefined,
        comments: drafts
          .filter((draft) => draft.message.trim())
          .map((draft) => ({
            message: draft.message.trim(),
            mediaAssetId: draft.mediaAssetId,
          })),
      }),
    onSuccess: (data) => {
      setResult(
        t('comments.queued', {
          count: data.queuedComments,
          page: data.pageName,
          seconds: Math.round(data.pacingMs / 1000),
          minutes: Math.max(1, Math.round(data.estimatedCompletionMs / 60_000)),
        }),
      );
      setDrafts([{ message: '' }]);
    },
  });

  const commentablePages = pages.data?.pages.filter((page) => page.canComment) ?? [];
  const validDrafts = drafts.filter((draft) => draft.message.trim()).length;
  const maxComments = capabilities.data?.maxCommentsPerBatch ?? 10;

  const uploadImage = async (index: number, file: File) => {
    const uploaded = await api.uploadMedia(file);
    setDrafts((current) =>
      current.map((draft, i) =>
        i === index
          ? {
              ...draft,
              mediaAssetId: uploaded.asset.id,
              mediaFilename: uploaded.asset.filename,
            }
          : draft,
      ),
    );
  };

  return (
    <section className="panel">
      <h2>{t('comments.title')}</h2>
      <p className="muted small">{t('comments.subtitle')}</p>

      <label htmlFor="comment-page">{t('comments.selectPage')}</label>
      <DestinationFilter
        id="comment-page"
        feature={DestinationFeature.COMMENTS}
        required
        value={pageId ? pageDestinationId(pageId) : ''}
        allLabel={t('comments.selectPagePlaceholder')}
        onChange={(id) => {
          setPageId(pageIdOf(id));
          setSourcePostId('');
          setParentCommentId(null);
        }}
      />

      {commentablePages.length === 0 && pages.data && (
        <div className="notice warn">{t('comments.noModerate')}</div>
      )}

      {/* Everything said on this Page, gathered under its posts.
          Choosing a post below is a filter for the composer, not a gate for
          reading: a Page with thirty posts should not need thirty visits. */}
      {pageId && <CommentThreads pageId={pageId} />}

      {posts.isError && <ErrorNotice error={posts.error} />}

      {posts.data && posts.data.posts.length > 0 && (
        <>
          <label htmlFor="comment-post">{t('comments.selectPost')}</label>
          <select
            id="comment-post"
            value={sourcePostId}
            onChange={(event) => {
              setSourcePostId(event.target.value);
              setParentCommentId(null);
            }}
          >
            <option value="">{t('comments.selectPostPlaceholder')}</option>
            {posts.data.posts.map((post) => (
              <option key={post.id} value={post.id}>
                {(post.message ?? t('common.noText')).slice(0, 70)}
              </option>
            ))}
          </select>
        </>
      )}

      {/* Reply flow — only when Meta has approved pages_read_user_content. */}
      {sourcePostId && !capabilities.data?.repliesEnabled && (
        <div className="notice">{t('comments.repliesDisabled')}</div>
      )}

      {sourcePostId && capabilities.data?.repliesEnabled && (
        <>
          {existing.isError && <ErrorNotice error={existing.error} />}
          {existing.data && existing.data.comments.length > 0 && (
            <>
              <label>{t('comments.replyTo')}</label>
              <ul className="target-list">
                <li>
                  <label>
                    <input
                      type="radio"
                      name="parent"
                      checked={parentCommentId === null}
                      onChange={() => setParentCommentId(null)}
                    />
                    {t('comments.replyToPost')}
                  </label>
                </li>
                {existing.data.comments.map((comment) => (
                  <li key={comment.id} className={comment.canReply ? '' : 'dim'}>
                    <label>
                      <input
                        type="radio"
                        name="parent"
                        disabled={!comment.canReply}
                        checked={parentCommentId === comment.id}
                        onChange={() => setParentCommentId(comment.id)}
                      />
                      <span className="small">
                        <strong>{comment.authorName ?? '—'}</strong>
                        {comment.fromPage && (
                          <span className="badge ok" style={{ marginInlineStart: 6 }}>
                            {t('comments.yourPage')}
                          </span>
                        )}{' '}
                        {comment.message ?? t('common.noText')}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {sourcePostId && (
        <>
          <label>
            {t('comments.drafts')} ({validDrafts}/{maxComments})
          </label>

          {drafts.map((draft, index) => (
            <div key={index} className="card">
              <div className="inline-form">
                <input
                  type="text"
                  value={draft.message}
                  placeholder={
                    index === 0
                      ? t('comments.placeholder')
                      : t('comments.followUp', { n: index + 1 })
                  }
                  onChange={(event) =>
                    setDrafts((current) =>
                      current.map((d, i) =>
                        i === index ? { ...d, message: event.target.value } : d,
                      ),
                    )
                  }
                />
                {drafts.length > 1 && (
                  <button
                    type="button"
                    className="ghost small"
                    onClick={() => setDrafts((current) => current.filter((_, i) => i !== index))}
                  >
                    {t('common.remove')}
                  </button>
                )}
              </div>

              <input
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadImage(index, file);
                  event.target.value = '';
                }}
              />
              {draft.mediaFilename && (
                <p className="muted tiny">
                  {t('composer.attached')}: {draft.mediaFilename}
                </p>
              )}
            </div>
          ))}

          {drafts.length < maxComments && (
            <button
              type="button"
              className="ghost"
              onClick={() => setDrafts((current) => [...current, { message: '' }])}
            >
              <Icon.plus />
              {t('comments.addAnother')}
            </button>
          )}

          {drafts.length > 1 && (
            <div className="notice">
              {t('comments.pacing', {
                count: drafts.length,
                seconds: Math.round((capabilities.data?.pacingMs ?? 45_000) / 1000),
              })}
            </div>
          )}

          {publish.isError && (
            <div className="notice error">
              {publish.error instanceof ApiError ? publish.error.message : t('publish.failed')}
            </div>
          )}
          {result && <div className="notice ok">{result}</div>}

          <button
            type="button"
            className="primary"
            onClick={() => publish.mutate()}
            disabled={validDrafts === 0 || publish.isPending}
          >
            <Icon.comment />
            {publish.isPending
              ? t('publish.queueing')
              : t('comments.publish', { count: validDrafts })}
          </button>
        </>
      )}
    </section>
  );
}

/**
 * Every comment on a Page, under the post it belongs to, collapsed by default.
 *
 * Read from our own rows rather than from Meta: the collector already stored
 * them, and asking again would be one request per post to show what we have.
 *
 * Posts with something still unanswered sort to the top and open themselves.
 * The rest stay shut, because thirty expanded threads is not a page anyone
 * reads — it is a wall to scroll past.
 */
function CommentThreads({ pageId }: { pageId: string }) {
  const { t, language } = useI18n();
  const locale = language === 'ar' ? 'ar' : 'en';
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const threads = useQuery({
    queryKey: ['comment-threads', pageId],
    queryFn: () => api.commentThreads(pageId),
    enabled: pageId !== '',
  });

  if (threads.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (threads.isError) return <ErrorNotice error={threads.error} />;

  const rows = threads.data?.threads ?? [];
  if (rows.length === 0) return <p className="muted small">{t('comments.noneStored')}</p>;

  return (
    <div className="threads">
      {rows.map((thread) => {
        // Anything waiting is open unless it was deliberately shut; anything
        // finished is shut unless it was deliberately opened.
        const expanded = open[thread.externalPostId] ?? thread.waiting > 0;

        return (
          <section className="thread" key={thread.externalPostId}>
            <button
              type="button"
              className="thread-head"
              aria-expanded={expanded}
              onClick={() =>
                setOpen((current) => ({
                  ...current,
                  [thread.externalPostId]: !expanded,
                }))
              }
            >
              <span className="thread-toggle">{expanded ? '−' : '+'}</span>
              <span className="thread-title">
                {thread.postExcerpt?.slice(0, 70) ?? t('common.noText')}
              </span>
              <span className="thread-counts">
                {thread.waiting > 0 && <span className="badge warn">{thread.waiting}</span>}
                <span className="muted small">
                  {t('comments.countOnPost', { count: thread.total })}
                </span>
              </span>
            </button>

            {expanded && (
              <ul className="inbox-list thread-body">
                {thread.comments.map((comment) => (
                  <InboxCard
                    key={comment.id}
                    item={comment}
                    locale={locale}
                    readOnly={comment.repliedAt !== null}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
